<script setup lang="ts">
import { nextTick, ref } from 'vue'
import { ShieldCheck, X } from '@lucide/vue'
import { useI18n } from 'vue-i18n'
import { api } from '@/services/api'
import { useAuthStore } from '@/stores/auth'
import type { OidcSettings } from '@/types/oidc'

const auth = useAuthStore()
const { t } = useI18n()
const dialog = ref<HTMLDialogElement | null>(null)
const settings = ref<OidcSettings | null>(null)
const subjects = ref('')
const emails = ref('')
const busy = ref(false)
const error = ref('')
const notice = ref('')

async function load(): Promise<void> {
  busy.value = true
  error.value = ''
  try {
    settings.value = {
      ...(await api.oidcSettings(auth.token)),
      client_secret: '',
      clear_client_secret: false,
    }
    subjects.value = settings.value.allowed_subjects.join('\n')
    emails.value = settings.value.allowed_emails.join('\n')
  } catch (reason) {
    error.value = reason instanceof Error ? reason.message : String(reason)
  } finally {
    busy.value = false
  }
}

async function open(): Promise<void> {
  notice.value = ''
  await nextTick()
  dialog.value?.showModal()
  await load()
}

function close(): void {
  if (settings.value) settings.value.client_secret = ''
  dialog.value?.close()
}

function entries(value: string): string[] {
  return value
    .split(/\n|,/)
    .map((item) => item.trim())
    .filter(Boolean)
}

async function save(): Promise<void> {
  if (!settings.value) return
  busy.value = true
  error.value = notice.value = ''
  try {
    const value = {
      ...settings.value,
      allowed_subjects: entries(subjects.value),
      allowed_emails: entries(emails.value),
      client_secret: settings.value.client_secret || null,
    }
    settings.value = {
      ...(await api.updateOidcSettings(auth.token, value)),
      client_secret: '',
      clear_client_secret: false,
    }
    notice.value = t('oidc.saved')
    if (auth.oidcAuthenticated) {
      await auth.logout().catch(() => undefined)
      window.location.assign('/login')
    }
  } catch (reason) {
    error.value = reason instanceof Error ? reason.message : String(reason)
  } finally {
    busy.value = false
  }
}

async function test(): Promise<void> {
  busy.value = true
  error.value = notice.value = ''
  try {
    await api.testOidc(auth.token)
    notice.value = t('oidc.testOk')
  } catch (reason) {
    error.value = reason instanceof Error ? reason.message : String(reason)
  } finally {
    busy.value = false
  }
}

async function copy(): Promise<void> {
  if (!settings.value) return
  try {
    await navigator.clipboard.writeText(settings.value.callback_url)
    notice.value = t('oidc.copied')
  } catch {
    error.value = settings.value.callback_url
  }
}
</script>

<template>
  <section class="panel mt-6 flex flex-wrap items-center justify-between gap-4 p-5">
    <div>
      <h2 class="flex items-center gap-2 font-display font-bold text-ink">
        <ShieldCheck class="size-5 text-moss-600" />{{ $t('oidc.title') }}
      </h2>
      <p class="mt-1 text-sm text-slate-500">{{ $t('oidc.description') }}</p>
    </div>
    <button type="button" class="btn-primary" @click="open">{{ $t('oidc.configure') }}</button>
  </section>
  <Teleport to="body">
    <dialog
      ref="dialog"
      aria-labelledby="oidc-dialog-title"
      class="panel m-auto max-h-[90vh] w-[min(94vw,760px)] overflow-auto p-0 backdrop:bg-ink/50"
      @close="settings && (settings.client_secret = '')"
    >
      <div
        class="flex items-center justify-between border-b border-slate-200 p-5 dark:border-slate-700"
      >
        <h2 id="oidc-dialog-title" class="font-display text-xl font-bold text-ink">
          {{ $t('oidc.title') }}
        </h2>
        <button
          type="button"
          class="rounded-md p-2 text-slate-500"
          :aria-label="$t('common.close')"
          @click="close"
        >
          <X class="size-5" />
        </button>
      </div>
      <form class="space-y-5 p-5 sm:p-7" @submit.prevent="save">
        <p class="text-sm leading-6 text-slate-500">{{ $t('oidc.bootstrap') }}</p>
        <p v-if="error" role="alert" class="rounded-md bg-red-50 p-3 text-sm text-red-700">
          {{ error }}
        </p>
        <p v-if="notice" role="status" class="text-sm text-moss-600">{{ notice }}</p>
        <template v-if="settings">
          <div class="rounded-md bg-slate-50 p-4 dark:bg-slate-800">
            <label for="oidc-callback" class="field-label">{{ $t('oidc.callback') }}</label>
            <input
              id="oidc-callback"
              :value="settings.callback_url"
              readonly
              class="field mt-2 font-mono text-xs"
            />
            <button type="button" class="mt-2 text-sm font-medium text-moss-600" @click="copy">
              {{ $t('oidc.copy') }}
            </button>
          </div>
          <label class="block"
            ><span class="field-label">{{ $t('nodeDisplay.logoutCallback') }}</span
            ><input
              readonly
              class="field mt-2 font-mono text-xs"
              :value="
                settings.logout_callback_url || settings.public_url + '/auth/logout/complete'
              "
          /></label>
          <label class="block"
            ><span class="field-label">{{ $t('oidc.issuer') }}</span
            ><input
              v-model.trim="settings.issuer"
              type="url"
              class="field mt-2"
              placeholder="https://your-erauth.example.com"
          /></label>
          <div class="grid gap-4 sm:grid-cols-2">
            <label class="block"
              ><span class="field-label">{{ $t('oidc.clientId') }}</span
              ><input v-model.trim="settings.client_id" class="field mt-2" autocomplete="off"
            /></label>
            <label class="block"
              ><span class="field-label">{{ $t('oidc.clientKey') }}</span
              ><input
                v-model="settings.client_secret"
                type="password"
                class="field mt-2"
                autocomplete="new-password"
                :placeholder="settings.client_secret_configured ? $t('oidc.keyConfigured') : ''"
            /></label>
          </div>
          <p class="text-xs leading-5 text-slate-500">{{ $t('oidc.keyHint') }}</p>
          <label v-if="settings.client_secret_configured" class="flex items-center gap-2 text-sm"
            ><input v-model="settings.clear_client_secret" type="checkbox" />{{
              $t('oidc.clearKey')
            }}</label
          >
          <div class="grid gap-4 sm:grid-cols-2">
            <label class="block"
              ><span class="field-label">{{ $t('oidc.scopes') }}</span
              ><input v-model.trim="settings.scopes" class="field mt-2" required
            /></label>
            <label class="block"
              ><span class="field-label">{{ $t('oidc.authMethod') }}</span
              ><select v-model="settings.token_endpoint_auth_method" class="field mt-2">
                <option value="client_secret_basic">client_secret_basic</option>
                <option value="client_secret_post">client_secret_post</option>
              </select></label
            >
          </div>
          <div class="grid gap-4 sm:grid-cols-2">
            <label class="block"
              ><span class="field-label">{{ $t('oidc.subjects') }}</span
              ><textarea
                v-model="subjects"
                class="field mt-2 font-mono text-sm"
                rows="3"
              ></textarea>
            </label>
            <label class="block"
              ><span class="field-label">{{ $t('oidc.emails') }}</span
              ><textarea v-model="emails" class="field mt-2 text-sm" rows="3"></textarea>
            </label>
          </div>
          <p class="text-xs text-slate-500">{{ $t('oidc.allowlistHint') }}</p>
          <label class="flex items-center gap-3 text-sm font-medium"
            ><input v-model="settings.enabled" type="checkbox" />{{ $t('oidc.enabled') }}</label
          >
          <div
            class="flex flex-wrap justify-end gap-3 border-t border-slate-200 pt-5 dark:border-slate-700"
          >
            <button type="button" class="btn-secondary" :disabled="busy" @click="test">
              {{ busy ? $t('oidc.testing') : $t('oidc.test') }}
            </button>
            <button type="submit" class="btn-primary" :disabled="busy">
              {{ busy ? $t('common.saving') : $t('oidc.save') }}
            </button>
          </div>
        </template>
        <p v-else-if="busy" class="text-sm text-slate-500">{{ $t('common.loading') }}</p>
        <button v-else type="button" class="btn-secondary" @click="load">
          {{ $t('common.tryAgain') }}
        </button>
      </form>
    </dialog>
  </Teleport>
</template>
